import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowLeft, FileText, TrendingUp, Send } from 'lucide-react';

export default function CreatorDashboardPage() {
  const [creatorId, setCreatorId] = useState('demo_creator');
  const [overview, setOverview] = useState(null);
  const [retention, setRetention] = useState(null);
  const [ltv, setLtv] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`/api/v2/analytics/overview?creator_id=${creatorId}`).then(r => r.json()).catch(() => null),
      fetch(`/api/v2/analytics/retention?creator_id=${creatorId}`).then(r => r.json()).catch(() => null),
      fetch(`/api/v2/analytics/ltv?creator_id=${creatorId}`).then(r => r.json()).catch(() => null),
      fetch(`/api/v2/analytics/insights?creator_id=${creatorId}`).then(r => r.json()).catch(() => null),
    ]).then(([o, r, l, i]) => {
      setOverview(o);
      setRetention(r);
      setLtv(l);
      setInsight(i);
      setLoading(false);
    });
  }, [creatorId]);

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FFD93D]" />
            <span className="font-semibold">Creator Dashboard</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-3">Cross-platform analytics that Substack doesn't show.</h1>
        <p className="text-lg text-[#94A3B8] mb-8 max-w-3xl">
          Cohort retention. LTV by acquisition source. Content → revenue attribution. Cross-platform unified view.
          Delivered via WhatsApp at 240-480 chars.
        </p>

        {loading ? (
          <div className="text-[#94A3B8]">Loading analytics...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                <Users className="w-5 h-5 text-[#3EF1C7] mb-2" />
                <div className="text-3xl font-bold">{overview?.audience_size?.toLocaleString() || 0}</div>
                <div className="text-xs text-[#64748B] mt-1">Audience</div>
              </div>
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                <FileText className="w-5 h-5 text-[#FFD93D] mb-2" />
                <div className="text-3xl font-bold">{overview?.content_count?.toLocaleString() || 0}</div>
                <div className="text-xs text-[#64748B] mt-1">Content pieces</div>
              </div>
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                <TrendingUp className="w-5 h-5 text-[#10B981] mb-2" />
                <div className="text-3xl font-bold text-[#10B981]">
                  ${overview?.lifetime_revenue_usd?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-[#64748B] mt-1">Lifetime revenue</div>
              </div>
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                <TrendingUp className="w-5 h-5 text-[#9B59B6] mb-2" />
                <div className="text-3xl font-bold text-[#9B59B6]">
                  ${ltv?.overall_ltv_usd?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-[#64748B] mt-1">Overall LTV</div>
              </div>
            </div>

            {insight && insight.insight && (
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6 mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-lg flex items-center gap-2">
                    <Send className="w-4 h-4 text-[#3EF1C7]" /> This week's insight (WhatsApp format)
                  </h2>
                  <span className="text-xs text-[#64748B]">{insight.char_count} chars</span>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-[#3EF1C7] font-mono">{insight.insight}</pre>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {retention && retention.cohorts && retention.cohorts.length > 0 && (
                <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6">
                  <h2 className="font-semibold mb-3">Cohort Retention</h2>
                  <div className="text-xs text-[#64748B] mb-2">% of cohort still engaged at month N</div>
                  <div className="space-y-2">
                    {retention.cohorts.slice(0, 6).map(c => (
                      <div key={c.cohort} className="flex items-center gap-3">
                        <span className="text-xs font-mono w-16 text-[#94A3B8]">{c.cohort}</span>
                        <div className="flex-1 flex gap-1">
                          {Object.entries(c.retention || {}).slice(0, 8).map(([off, pct]) => (
                            <div
                              key={off}
                              className="h-6 rounded text-[10px] flex items-center justify-center"
                              style={{ flex: 1, background: `rgba(62, 241, 199, ${pct})`, color: pct > 0.5 ? '#0B1120' : '#94A3B8' }}
                            >
                              {Math.round(pct * 100)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ltv && (
                <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6">
                  <h2 className="font-semibold mb-3">LTV Breakdown</h2>
                  <div className="text-xs text-[#64748B] mb-3">
                    Total revenue: ${ltv.total_revenue_usd?.toLocaleString()} • Audience: {ltv.total_audience?.toLocaleString()}
                  </div>
                  {ltv.by_platform && Object.keys(ltv.by_platform).length > 0 && (
                    <div>
                      <div className="text-xs text-[#94A3B8] mb-2">By platform</div>
                      {Object.entries(ltv.by_platform).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-sm py-1">
                          <span className="text-[#94A3B8]">{k}</span>
                          <span className="font-mono">${v?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
